import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { BackLink } from "@/components/BackLink";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Loader2, Receipt, FileText, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAY = `${API}/academy/coach-pay`;

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const dayLabel = (d) =>
  d ? new Date(`${d}T00:00:00`).toLocaleDateString("en-SG",
    { weekday: "short", day: "numeric", month: "short" }) : "-";

const STATUS_TONE = {
  submitted: "bg-amber-500/15 text-amber-300",
  approved: "bg-sky-500/15 text-sky-300",
  paid: "bg-emerald-500/15 text-emerald-300",
  rejected: "bg-red-500/15 text-red-300",
};

export default function CoachPay() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState({ start: "", end: "" });
  const [editing, setEditing] = useState(false);
  const [details, setDetails] = useState({
    name: "", address: "", contact: "", uen_or_nric: "", payment_details: "" });
  const [busy, setBusy] = useState(false);

  const load = async (params = {}) => {
    setLoading(true);
    try {
      const r = await axios.get(`${PAY}/me`, { params, withCredentials: true });
      setData(r.data);
      setRange({ start: r.data.start_date, end: r.data.end_date });
      if (r.data.contractor_details?.name) setDetails(r.data.contractor_details);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load your pay");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveDetails = async () => {
    setBusy(true);
    try {
      await axios.put(`${PAY}/me/details`, details, { withCredentials: true });
      toast.success("Invoice details saved");
      setEditing(false);
      load({ start_date: range.start, end_date: range.end });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save those details");
    } finally {
      setBusy(false);
    }
  };

  const raiseInvoice = async (body) => {
    setBusy(true);
    try {
      const r = await axios.post(`${PAY}/me/invoice`, body, { withCredentials: true });
      toast.success(`Invoice ${r.data.invoice.number} sent · ${money(r.data.invoice.total)}`);
      load({ start_date: range.start, end_date: range.end });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not raise that invoice");
    } finally {
      setBusy(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 pb-16">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <BackLink to="/academy/coach-dashboard" label="Back"
          className="text-slate-400 hover:text-white -ml-2 mb-2"
          testId="my-pay-back" />
        <h1 className="text-3xl sm:text-4xl font-bold text-white">My pay</h1>
        <p className="text-sm text-slate-400 mb-6">
          Your approved sessions, and the invoice you raise to {data?.bill_to?.name}.
        </p>

        <Card className="p-5 bg-slate-900/60 border-2 border-amber-500/20 mb-4">
          <p className="text-xs text-slate-400">Ready to invoice</p>
          <p className="text-4xl font-bold text-white" data-testid="pay-total">
            {money(data?.total)}
          </p>
          <p className="text-xs text-slate-400 mt-1">
            {data?.sessions?.length || 0} session{data?.sessions?.length === 1 ? "" : "s"} ·{" "}
            {data?.hours || 0} hours · {dayLabel(range.start)} to {dayLabel(range.end)}
          </p>
          {!!data?.pending?.length && (
            <p className="text-xs text-amber-300 mt-2" data-testid="pay-pending">
              <Clock className="w-3 h-3 inline mr-1" />
              {data.pending.length} check-in{data.pending.length === 1 ? "" : "s"} still
              awaiting the academy's approval
            </p>
          )}
          {data?.missing_rate && (
            <p className="text-xs text-red-300 mt-2" data-testid="pay-no-rate">
              No pay rate has been set for you yet — ask the academy to set it.
            </p>
          )}
        </Card>

        <div className="flex flex-wrap gap-2 mb-4">
          {[["Last week", "week"], ["Last month", "month"]].map(([label, p]) => (
            <Button key={p} size="sm" variant="outline"
              onClick={() => load({ period: p })}
              data-testid={`range-${p}`}>{label}</Button>
          ))}
          <div className="flex items-center gap-2">
            <input type="date" value={range.start}
              onChange={(e) => setRange({ ...range, start: e.target.value })}
              className="bg-slate-800 border border-white/10 rounded px-2 py-1.5 text-sm text-white"
              data-testid="pay-from" />
            <input type="date" value={range.end}
              onChange={(e) => setRange({ ...range, end: e.target.value })}
              className="bg-slate-800 border border-white/10 rounded px-2 py-1.5 text-sm text-white"
              data-testid="pay-to" />
            <Button size="sm" variant="outline"
              onClick={() => load({ start_date: range.start, end_date: range.end })}
              data-testid="pay-apply">Show</Button>
          </div>
        </div>

        <Card className="bg-slate-900/50 border-2 border-white/5 mb-4 overflow-hidden">
          {!data?.sessions?.length ? (
            <p className="p-5 text-sm text-slate-400 text-center" data-testid="pay-sessions-empty">
              Nothing to invoice in these dates.
            </p>
          ) : (
            <div data-testid="pay-session-list">
              {data.sessions.map((s) => (
                <div key={s.session_id}
                  className="flex items-center justify-between px-4 py-3 border-b border-white/5 last:border-0"
                  data-testid={`pay-session-${s.session_id}`}>
                  <div className="min-w-0">
                    <p className="text-sm text-slate-200">
                      {dayLabel(s.date)} {s.start_time || ""}
                    </p>
                    <p className="text-xs text-slate-500 truncate">
                      {s.team} · {s.hours}h ·{" "}
                      {s.rate ? `${money(s.rate)} ${s.basis === "per_hour" ? "/hr" : "/session"}` : "no rate"}
                    </p>
                  </div>
                  <span className="text-sm font-semibold text-white">{money(s.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-5 bg-slate-900/50 border-2 border-white/5 mb-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-white">My invoice details</p>
            <button onClick={() => setEditing(!editing)}
              className="text-xs text-amber-300 underline"
              data-testid="edit-details">
              {editing ? "Cancel" : data?.details_complete ? "Edit" : "Add"}
            </button>
          </div>
          {!editing ? (
            data?.details_complete ? (
              <p className="text-xs text-slate-400 whitespace-pre-line" data-testid="details-summary">
                {details.name}
                {"\n"}{details.address}
                {details.uen_or_nric ? `\nUEN / NRIC: ${details.uen_or_nric}` : ""}
                {details.payment_details ? `\nPay to: ${details.payment_details}` : ""}
              </p>
            ) : (
              <p className="text-xs text-amber-300" data-testid="details-missing">
                Add your name and address once — every invoice uses them after that.
              </p>
            )
          ) : (
            <div className="space-y-2">
              {[["name", "Your name or company"], ["address", "Address"],
                ["contact", "Phone or email"], ["uen_or_nric", "UEN or NRIC (optional)"],
                ["payment_details", "Where to pay you (optional)"]].map(([key, ph]) => (
                <Input key={key} value={details[key] || ""} placeholder={ph}
                  onChange={(e) => setDetails({ ...details, [key]: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid={`detail-${key}`} />
              ))}
              <Button size="sm" onClick={saveDetails} disabled={busy}
                className="bg-amber-500 hover:bg-amber-600 text-slate-950"
                data-testid="save-details">Save</Button>
            </div>
          )}
        </Card>

        <div className="flex flex-wrap gap-2 mb-6">
          <Button onClick={() => raiseInvoice({ period: "week" })}
            disabled={busy || !data?.details_complete}
            className="bg-amber-500 hover:bg-amber-600 text-slate-950"
            data-testid="invoice-week">
            <Receipt className="w-4 h-4 mr-1" /> Invoice last week
          </Button>
          <Button onClick={() => raiseInvoice({ period: "month" })}
            disabled={busy || !data?.details_complete}
            variant="outline" data-testid="invoice-month">
            Invoice last month
          </Button>
          <Button onClick={() => raiseInvoice({ start_date: range.start, end_date: range.end })}
            disabled={busy || !data?.details_complete || !data?.sessions?.length}
            variant="outline" data-testid="invoice-range">
            Invoice these dates
          </Button>
        </div>

        <h2 className="text-base md:text-lg font-semibold text-white mb-2">My invoices</h2>
        {!data?.invoices?.length ? (
          <Card className="p-5 bg-slate-900/50 border-2 border-white/5 text-center text-sm text-slate-400"
            data-testid="invoices-empty">
            None yet.
          </Card>
        ) : (
          <div className="space-y-2" data-testid="invoice-list">
            {data.invoices.map((inv) => (
              <Card key={inv.id} className="p-4 bg-slate-900/50 border-2 border-white/5"
                data-testid={`invoice-${inv.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm text-slate-200">
                      <FileText className="w-3 h-3 inline mr-1" />
                      {inv.number}
                      <Badge className={`ml-2 ${STATUS_TONE[inv.status] || ""}`}>
                        {inv.status}
                      </Badge>
                    </p>
                    <p className="text-xs text-slate-500">
                      {dayLabel(inv.period_start)} to {dayLabel(inv.period_end)} ·{" "}
                      {inv.session_count} session{inv.session_count === 1 ? "" : "s"}
                      {inv.payment_reference ? ` · ${inv.payment_reference}` : ""}
                    </p>
                  </div>
                  <span className="text-sm font-semibold text-white whitespace-nowrap">
                    {money(inv.total)}
                  </span>
                </div>
                <a href={`${PAY}/invoices/${inv.id}/document`} target="_blank" rel="noreferrer"
                  className="inline-block mt-2 text-xs text-amber-300 underline"
                  data-testid={`invoice-print-${inv.id}`}>
                  Open invoice to print or save
                </a>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
